import type { RefObject } from 'react';
import { useEffect } from 'react';

type TUseClickOutside<T> = {
  /** Determines whether listener is added or removed */
  active: boolean;
  /** The component ref that clicks outside are being listened for */
  ref: RefObject<T>;
  /** The callback for when a click outside occurs */
  onClickOutside: () => void;
};

/**
 * A hook that listens for clicks outside of a ref and calls back
 */
const useClickOutside = <T,>({
  active,
  ref,
  onClickOutside,
}: TUseClickOutside<T>) => {
  useEffect(() => {
    if (active) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const handleClickOutside = (event: MouseEvent | KeyboardEvent) => {
    if (
      ref.current &&
      (ref.current as unknown as HTMLElement).contains(event.target as Node)
    ) {
      return;
    }
    onClickOutside();
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClickOutside();
      // Ensure focus does not persist once action is completed
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }
  };
};

export { useClickOutside };
